import { mdiLeak } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";
import { FunctionComponent } from "react";
import { Link } from "react-router-dom";

import { IconProps } from "@mdi/react/dist/IconProps";
import clsx from "clsx";
import {
  ClipboardClockSVGIcon,
  DollarCircleSvgIcon,
  DollarDocSvgIcon,
  HistorySvgIcon,
  NewLabelSVGIcon
} from "components/SVGIcons";
import { formatNumber } from "helpers/formatNumber";
import { GridCardItem } from "models/Base";
import { IManagePayItem } from "models/HaulPay";
import { CARRIER_FR_STATUS_LABELS, COLOR_BY_STATUS } from "./base";
import { routes } from "./routes";
import PaymentSpeedInput from "../components/PaymentSpeedInput";

export const CARRIER_ITEMS: GridCardItem[] = [
  {
    id: 0,
    label: "Request Pay",
    icon: (props: React.SVGProps<SVGSVGElement>) => <DollarDocSvgIcon {...props} />,
    link: routes.HAULPAY_CARRIER_REQUEST_PAY
  },
  {
    id: 1,
    label: "Manage Pay",
    icon: (props: React.SVGProps<SVGSVGElement>) => <HistorySvgIcon {...props} />,
    link: routes.HAULPAY_CARRIER_MANAGE_PAY
  },
  {
    id: 2,
    label: "Connections",
    icon: (props: FunctionComponent<IconProps>) => <Icon path={mdiLeak} {...props} />,
    link: routes.HAULPAY_CARRIER_PAYMENT_PREFERENCE
  },
  {
    id: 3,
    label: "Assigned",
    icon: (props: React.SVGProps<SVGSVGElement>) => <NewLabelSVGIcon {...props} />,
    link: routes.HAULPAY_ASSIGNED_LOADS
  },
  {
    id: 4,
    label: "Credit",
    icon: (props: React.SVGProps<SVGSVGElement>) => <DollarCircleSvgIcon {...props} />,
    link: routes.HAULPAY_CREDIT
  }
];

export const HISTORY_TAB_ITEMS = [
  {
    id: 0,
    label: "In Process",
    value: "in-process",
    link: routes.HAULPAY_CARRIER_HISTORY_IN_PROCESS
  },
  {
    id: 1,
    label: "Incomplete",
    value: "incomplete",
    link: ""
  }
];

export const HISTORY_DATA: IManagePayItem[] = [
  {
    id: 0,
    title: "All",
    count: "72",
    totalAmount: 18300,
    link: routes.HAULPAY_CARRIER_MANAGE_PAY
  },
  {
    id: 1,
    title: "In Process",
    count: "2",
    totalAmount: 500,
    link: routes.HAULPAY_CARRIER_HISTORY_IN_PROCESS
  },
  {
    id: 2,
    title: "Pending",
    count: "12",
    totalAmount: 1300,
    link: routes.HAULPAY_CARRIER_MANAGE_PAY
  },
  {
    id: 3,
    title: "Incomplete",
    count: "10",
    totalAmount: 5000,
    link: routes.HAULPAY_CARRIER_MANAGE_PAY
  },
  {
    id: 4,
    title: "Doc Review",
    count: "12",
    totalAmount: 1300,
    link: routes.HAULPAY_CARRIER_MANAGE_PAY
  },
  {
    id: 5,
    title: "Non Factored",
    count: "12",
    totalAmount: 1300,
    link: routes.HAULPAY_CARRIER_MANAGE_PAY
  },
  {
    id: 6,
    title: "Declined",
    count: "2",
    totalAmount: 570,
    link: routes.HAULPAY_CARRIER_MANAGE_PAY
  },
  {
    id: 7,
    title: "Paid",
    count: "8",
    totalAmount: 1300,
    link: routes.HAULPAY_CARRIER_MANAGE_PAY
  }
];

export const HISTORY_TABLE_HEADER_LIST = [
  {
    value: ["status", "id"],
    label: "Status",
    render: (item: any) => (
      <Link
        to={`${routes.HAULPAY_CARRIER_MANGE_PAY_PAYMENT_DETAILS}/${item?.id}`}
        className={clsx(
          "rounded-3xl justify-center px-3.5 py-1.5 text-xs flex items-center gap-1.5 border",
          COLOR_BY_STATUS[item?.status]
        )}>
        <ClipboardClockSVGIcon className="flex-none size-5" />
        <span>{item?.status}</span>
      </Link>
    )
  },
  {
    value: "debtor.name",
    label: "Bill To"
  },
  {
    value: "amount",
    label: "Invoice Amount",
    render: (item: number) => <span>{item ? formatNumber(item, 2) : "-"}</span>
  },
  {
    value: "invoiceNumber",
    label: "Invoice #"
  },
  {
    value: "userLoadNumber",
    label: "Load #"
  },
  {
    value: "created",
    label: "Requested At",
    render: (item: string) => <span>{dayjs(item).format("MM/DD/YYYY hh:mm A")}</span>
  },
  {
    value: "invoiceDate",
    label: "Invoiced At",
    render: (item: string) => (item ? <span>{dayjs(item).format("MM/DD/YYYY hh:mm A")}</span> : "-")
  },
  {
    value: "fundedAt",
    label: "Funded At",
    render: (item: string) => (item ? <span>{dayjs(item).format("MM/DD/YYYY hh:mm A")}</span> : "-")
  },
  {
    value: "discountRate",
    label: "Rate",
    render: (item: number) => <span>{Number(item) ? `${item}%` : "-"}</span>
  },
  {
    value: "amountFunded",
    label: "Payment Amount",
    render: (item: number) => <span>{item ? formatNumber(item, 2) : "-"}</span>
  },
  {
    value: "id",
    label: "Details",
    render: (item: string) => (
      <Link
        to={`${routes.HAULPAY_CARRIER_MANGE_PAY_PAYMENT_DETAILS}/${item}`}
        className="px-4 py-3 border border-primary rounded-base text-primary bg-background">
        More
      </Link>
    )
  }
];

export const ASSIGNED_LOADS_TABLE_HEADER_LIST = [
  {
    value: "status",
    label: "Status",
    render: (item: string) => (
      <div
        className={clsx(
          "rounded-3xl justify-center px-3.5 py-1.5 text-xs flex items-center gap-1.5 border",
          COLOR_BY_STATUS[item]
        )}>
        <ClipboardClockSVGIcon className="flex-none size-5" />
        <span>{CARRIER_FR_STATUS_LABELS[item?.toLowerCase().replace(/\s+/g, "_")]}</span>
      </div>
    )
  },
  {
    value: "debtor.name",
    label: "Bill To"
  },
  {
    value: "amount",
    label: "Invoice Amount",
    render: (item: number) => <span>{item ? formatNumber(item, 2) : "-"}</span>
  },
  {
    value: "invoiceNumber",
    label: "Invoice #"
  },
  {
    value: "userLoadNumber",
    label: "Load #"
  },
  {
    value: "created",
    label: "Requested At",
    render: (item: string) => <span>{dayjs(item).format("MM/DD/YYYY hh:mm A")}</span>
  },
  {
    value: "fundedAt",
    label: "Funded At",
    render: (item: string) => <span>{item ? dayjs(item).format("MM/DD/YYYY hh:mm A") : "-"}</span>
  },
  {
    value: "discountRate",
    label: "Rate",
    render: (item: number) => <span>{item ? `${item}%` : "-"}</span>
  },
  {
    value: "amountFunded",
    label: "Payment Amount",
    render: (item: number) => <span>{item ? formatNumber(item, 2) : "-"}</span>
  },
  {
    value: ["payoutDays", "factoring"],
    label: "Payment Speed",
    render: (item: any) => (
      <PaymentSpeedInput
        noLabel
        name="payment_speed"
        inputProps={{
          disabled: true,
          btnClass: "!border-subtitle !rounded-lg !bg-background",
          dropdownClass: "!px-3 !text-sm",
          value: CARRIER_PAYMENT_SPEED.find((option) => option.value === item?.payoutDays)
        }}
        factoringId={item?.factoring?.id}
      />
    )
  },
  {
    value: "id",
    label: "Details",
    render: (item: string) => (
      <Link
        to={`${routes.HAULPAY_ASSIGNED_LOADS_PAYMENT_DETAILS}/${item}`}
        className="px-4 py-3 border border-primary rounded-base text-primary bg-background">
        More
      </Link>
    )
  }
];

export const CARRIER_PAYMENT_SPEED = [
  {
    id: 0,
    label: "1 Day (3% Quick Pay Fee)",
    value: "ach_1_day",
    day: 1,
    amount: 3
  },
  {
    id: 1,
    label: "7 Days (2.5% Quick Pay Fee)",
    value: "ach_7_day",
    day: 7,
    amount: 2.5
  },
  {
    id: 2,
    label: "15 Days (2% Quick Pay Fee)",
    value: "ach_15_day",
    day: 15,
    amount: 2
  },
  {
    id: 3,
    label: "21 Days (1.5% Quick Pay Fee)",
    value: "ach_21_day",
    day: 21,
    amount: 1.5
  },
  {
    id: 4,
    label: "30 Days (1% Quick Pay Fee)",
    value: "ach_30_day",
    day: 30,
    amount: 1
  }
];

export const CARRIER_FR_CSV_HEADER = [
  { label: "Invoice #", key: "invoiceNumber" },
  { label: "Load #", key: "userLoadNumber" },
  { label: "Invoice Date", key: "created" },
  { label: "Status", key: "status" },
  { label: "Debtor", key: "debtor" },
  { label: "Invoice Amount", key: "amount" },
  { label: "Fuel Advance Funded Date", key: "fundedAt" },
  { label: "Fuel Advance Payment #", key: "fuelAdvancePaymentNumber" },
  { label: "Fuel Advance Fee", key: "fuelAdvanceFee" },
  { label: "Fuel Advance Payment Amount", key: "fuelAdvanceAmount" },
  { label: "Delivery Advance Payment Date", key: "modified" },
  { label: "Discount Rate (%)", key: "discountRate" },
  { label: "Discount Rate ($)", key: "discountAmount" },
  { label: "Chargeback", key: "chargeback" },
  { label: "Giveback", key: "giveback" },
  { label: "Payment #", key: "paymentNumber" },
  { label: "Total Paid", key: "totalPay" },
  { label: "Client Invoice #", key: "clientInvoiceNumber" }
];
